"use client";

import { useRef, useState, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Code2,
  Palette,
  Smartphone,
  Video,
  Mic,
  Play,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const demos = [
  {
    id: "coding",
    icon: Code2,
    title: "Vibe Coding",
    subtitle: "Watch AI write a complete React component",
    color: "#3b82f6",
    gradient: "from-blue-500 to-cyan-500",
    code: `// AI-Generated Component
import { useState } from 'react';
import { motion } from 'framer-motion';

export function PricingCard({ plan }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="p-6 rounded-2xl bg-gradient-to-br"
      whileHover={{ scale: 1.02, y: -5 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <h3 className="text-2xl font-bold">
        {plan.name}
      </h3>
      <p className="text-4xl font-black mt-4">
        \${plan.price}/mo
      </p>
      <button className="w-full mt-6 py-3
        bg-gradient-to-r from-violet-600
        to-cyan-600 rounded-xl font-bold">
        Get Started
      </button>
    </motion.div>
  );
}`,
  },
  {
    id: "designer",
    icon: Palette,
    title: "Vibe Designer",
    subtitle: "UI components materialize from text",
    color: "#8b5cf6",
    gradient: "from-violet-500 to-purple-500",
    preview: "design",
  },
  {
    id: "mobile",
    icon: Smartphone,
    title: "Vibe Mobile",
    subtitle: "Cross-platform apps built instantly",
    color: "#06b6d4",
    gradient: "from-cyan-500 to-teal-500",
    preview: "mobile",
  },
  {
    id: "motion",
    icon: Video,
    title: "Vibe Motion",
    subtitle: "Video content assembled automatically",
    color: "#ec4899",
    gradient: "from-pink-500 to-rose-500",
    preview: "video",
  },
  {
    id: "voice",
    icon: Mic,
    title: "Vibe Voice",
    subtitle: "Voice commands bring ideas to life",
    color: "#10b981",
    gradient: "from-emerald-500 to-green-500",
    preview: "audio",
  },
];

function CodeTypingDemo({ code, color }: { code: string; color: string }) {
  const [displayedCode, setDisplayedCode] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) {
      setDisplayedCode(code);
      setIsTyping(false);
      return;
    }

    setDisplayedCode("");
    setIsTyping(true);
    let index = 0;

    const interval = setInterval(() => {
      if (index < code.length) {
        setDisplayedCode(code.slice(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(interval);
      }
    }, 15);

    return () => clearInterval(interval);
  }, [code, reducedMotion]);

  return (
    <div className="bg-[#0d1117] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
      {/* Editor header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#161b22] border-b border-white/10">
        <div className="flex gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <span className="ml-4 text-xs text-white/40 font-mono">PricingCard.tsx</span>
        <div className="ml-auto flex items-center gap-2 text-xs text-white/40">
          <Sparkles className="w-3 h-3 text-violet-400" />
          <span>AI Writing...</span>
        </div>
      </div>

      {/* Code area */}
      <div className="p-6 font-mono text-sm h-[350px] overflow-auto">
        <pre className="whitespace-pre-wrap">
          <code className="text-white/80">
            {displayedCode}
            {isTyping && (
              <motion.span
                className="inline-block w-2 h-5 ml-0.5"
                style={{ backgroundColor: color }}
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
            )}
          </code>
        </pre>
      </div>
    </div>
  );
}

function DesignPreview({ color, gradient }: { color: string; gradient: string }) {
  return (
    <div className="bg-[#0d1117] rounded-2xl overflow-hidden border border-white/10 shadow-2xl h-[350px] p-6">
      <div className="grid grid-cols-2 gap-4 h-full">
        {[1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            className={`rounded-xl border border-white/10 bg-gradient-to-br ${gradient} p-0.5`}
            initial={{ scale: 0, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ delay: i * 0.15, duration: 0.5, type: "spring" }}
          >
            <div className="w-full h-full rounded-xl bg-[#0d1117] p-4">
              <motion.div
                className="w-8 h-8 rounded-lg mb-3"
                style={{ backgroundColor: `${color}30` }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
              />
              <div className="space-y-2">
                <div className="h-2 bg-white/20 rounded w-3/4" />
                <div className="h-2 bg-white/10 rounded w-1/2" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function MobilePreview({ color, gradient }: { color: string; gradient: string }) {
  return (
    <div className="flex justify-center items-center h-[350px]">
      <motion.div
        className={`w-48 h-80 rounded-[3rem] bg-gradient-to-br ${gradient} p-[3px] shadow-2xl`}
        initial={{ y: 100, opacity: 0, rotateY: -30 }}
        animate={{ y: 0, opacity: 1, rotateY: 0 }}
        transition={{ duration: 0.8, type: "spring" }}
        style={{ perspective: "1000px" }}
      >
        <div className="w-full h-full rounded-[2.8rem] bg-[#0d1117] overflow-hidden">
          {/* Notch */}
          <div className="h-8 bg-[#161b22] flex items-center justify-center">
            <div className="w-20 h-4 rounded-full bg-black" />
          </div>
          {/* Content */}
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                className="h-12 rounded-xl"
                style={{ backgroundColor: `${color}20` }}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 + i * 0.1, duration: 0.4 }}
              />
            ))}
            <motion.div
              className={`h-12 rounded-xl bg-gradient-to-r ${gradient}`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1, type: "spring" }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function VideoPreview({ color, gradient }: { color: string; gradient: string }) {
  return (
    <div className="bg-[#0d1117] rounded-2xl overflow-hidden border border-white/10 shadow-2xl h-[350px] p-6 flex flex-col">
      {/* Video preview area */}
      <div className="flex-1 rounded-xl bg-[#161b22] flex items-center justify-center mb-4 relative overflow-hidden">
        <motion.div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at center, ${color}20 0%, transparent 70%)`,
          }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className={`w-20 h-20 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center cursor-pointer`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <Play className="w-8 h-8 text-white ml-1" />
        </motion.div>
      </div>
      {/* Timeline */}
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <motion.div
            key={i}
            className={`flex-1 h-12 rounded-lg bg-gradient-to-r ${gradient}`}
            style={{ opacity: 0.3 + i * 0.15 }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: i * 0.1, duration: 0.4 }}
          />
        ))}
      </div>
    </div>
  );
}

function AudioPreview({ color, gradient }: { color: string; gradient: string }) {
  return (
    <div className="bg-[#0d1117] rounded-2xl overflow-hidden border border-white/10 shadow-2xl h-[350px] flex items-center justify-center p-6">
      <div className="flex items-end gap-1 h-40">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.div
            key={i}
            className={`w-2 rounded-t bg-gradient-to-t ${gradient}`}
            animate={{
              height: [
                20 + Math.random() * 40,
                60 + Math.random() * 80,
                20 + Math.random() * 40,
              ],
            }}
            transition={{
              duration: 0.4 + Math.random() * 0.4,
              repeat: Infinity,
              delay: i * 0.03,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function DemoSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });
  const [activeDemo, setActiveDemo] = useState(0);
  const reducedMotion = useReducedMotion();

  // Auto-rotate demos
  useEffect(() => {
    if (reducedMotion) return;

    const interval = setInterval(() => {
      setActiveDemo((prev) => (prev + 1) % demos.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [reducedMotion]);

  const currentDemo = demos[activeDemo];

  return (
    <section
      ref={ref}
      id="demo"
      className="relative min-h-screen w-full py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e] via-[#0f0f23] to-[#16213e]" />

        {/* Dynamic glow based on active demo */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${currentDemo.color}30 0%, transparent 70%)`,
            filter: "blur(100px)",
          }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 5, repeat: Infinity }}
          key={currentDemo.id}
        />
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            className="inline-flex items-center gap-2 px-5 py-2 bg-white/5 border border-white/10 rounded-full mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            <Play className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-medium text-white/80">See It In Action</span>
          </motion.div>

          <motion.h2
            className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <span className="bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-transparent">
              WATCH THE MAGIC
            </span>
          </motion.h2>
        </div>

        {/* Demo Carousel */}
        <div className="max-w-5xl mx-auto">
          {/* Demo tabs */}
          <div className="flex justify-center gap-2 mb-10 flex-wrap">
            {demos.map((demo, index) => (
              <motion.button
                key={demo.id}
                onClick={() => setActiveDemo(index)}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl border transition-all ${
                  activeDemo === index
                    ? "border-white/30 bg-white/10"
                    : "border-white/10 hover:border-white/20 bg-white/5"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <demo.icon
                  className="w-5 h-5"
                  style={{ color: activeDemo === index ? demo.color : "rgba(255,255,255,0.5)" }}
                />
                <span className={`text-sm font-medium hidden md:inline ${
                  activeDemo === index ? "text-white" : "text-white/50"
                }`}>
                  {demo.title}
                </span>
              </motion.button>
            ))}
          </div>

          {/* Demo content */}
          <div className="relative">
            {/* Navigation arrows */}
            <button
              onClick={() =>
                setActiveDemo((prev) => (prev - 1 + demos.length) % demos.length)
              }
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 w-12 h-12 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm flex items-center justify-center hover:bg-white/10 transition-colors z-10 hidden md:flex"
            >
              <ChevronLeft className="w-6 h-6 text-white/60" />
            </button>
            <button
              onClick={() => setActiveDemo((prev) => (prev + 1) % demos.length)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 w-12 h-12 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm flex items-center justify-center hover:bg-white/10 transition-colors z-10 hidden md:flex"
            >
              <ChevronRight className="w-6 h-6 text-white/60" />
            </button>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentDemo.id}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -30, scale: 0.95 }}
                transition={{ duration: reducedMotion ? 0 : 0.4 }}
              >
                {/* Demo title */}
                <div className="text-center mb-8">
                  <motion.h3
                    className={`text-3xl font-bold mb-2 bg-gradient-to-r ${currentDemo.gradient} bg-clip-text text-transparent`}
                  >
                    {currentDemo.title}
                  </motion.h3>
                  <p className="text-white/50">
                    {currentDemo.subtitle}
                  </p>
                </div>

                {/* Demo preview */}
                {currentDemo.code ? (
                  <CodeTypingDemo
                    code={currentDemo.code}
                    color={currentDemo.color}
                  />
                ) : currentDemo.preview === "design" ? (
                  <DesignPreview color={currentDemo.color} gradient={currentDemo.gradient} />
                ) : currentDemo.preview === "mobile" ? (
                  <MobilePreview color={currentDemo.color} gradient={currentDemo.gradient} />
                ) : currentDemo.preview === "video" ? (
                  <VideoPreview color={currentDemo.color} gradient={currentDemo.gradient} />
                ) : currentDemo.preview === "audio" ? (
                  <AudioPreview color={currentDemo.color} gradient={currentDemo.gradient} />
                ) : null}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-3 mt-10">
            {demos.map((demo, index) => (
              <button
                key={demo.id}
                onClick={() => setActiveDemo(index)}
                className="relative"
              >
                <div
                  className={`w-3 h-3 rounded-full transition-all ${
                    activeDemo === index ? "scale-125" : "scale-100"
                  }`}
                  style={{
                    backgroundColor: activeDemo === index ? demo.color : "rgba(255,255,255,0.2)",
                  }}
                />
                {activeDemo === index && !reducedMotion && (
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundColor: demo.color }}
                    initial={{ scale: 1, opacity: 0.8 }}
                    animate={{ scale: 2.5, opacity: 0 }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default DemoSection;
